import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faCalculator,
  faCheck,
  faChevronDown,
  faChevronRight,
  faEye,
  faPlus,
  faSave,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import GeneralCostEditorForm, {
  createEmptyGeneralCostForm,
} from "../components/GeneralCostEditorForm";
import BridgeSelectionManager from "../components/BridgeSelectionManager";
import RepairProjectMobilizationConfigPanel from "../components/RepairProjectMobilizationConfigPanel";
import { useCustomLoading } from "../hooks/useCustomLoading";
import { calculateGeneralCostBaseTotal } from "../utils/mobilizationSimulationUtils";

const FALLBACK_WEEKS_LIMIT = "35";
const DAMAGE_ALLOCATION_MODES = Object.freeze({
  DAMAGE_SPLIT: "damage_split",
  ELEMENT_DAMAGE_LEVEL: "element_damage_level",
});
const isDamageSplitMode = (scenarioConfig) =>
  (scenarioConfig?.damageAllocationMode ||
    DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL) ===
  DAMAGE_ALLOCATION_MODES.DAMAGE_SPLIT;
const DEFAULT_SCENARIO = {
  damageAllocationMode: DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL,
  damageSplit: { A: 70, B: 20, C: 10 },
  damageSplitByCategory: {},
  estimatedPaintedAreaPercent: 30,
  foremansCountLimit: 1,
  layersPerDay: 1,
};

const STEPS = [
  {
    id: 1,
    title: "Project Details",
    description: "Bridge, planning year, and project metadata.",
  },
  {
    id: 2,
    title: "Elements Selection",
    description: "Choose the repair scope from the hierarchy lists.",
  },
  {
    id: 3,
    title: "Costs Review",
    description: "Review derived teams, week limit, and final estimate.",
  },
];

function areIdListsEqual(left = [], right = []) {
  if (left === right) return true;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (String(left[index]) !== String(right[index])) {
      return false;
    }
  }

  return true;
}

function normalizeWeeksLimit(value, fallback = FALLBACK_WEEKS_LIMIT) {
  const parsedValue = Number(value);
  if (Number.isFinite(parsedValue) && parsedValue > 0) {
    return String(Math.round(parsedValue));
  }
  return fallback;
}

function parseWeekLimit(value) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? Math.round(parsedValue)
    : null;
}

function createDefaultScenario() {
  return {
    ...DEFAULT_SCENARIO,
    damageSplit: { ...DEFAULT_SCENARIO.damageSplit },
    damageSplitByCategory: {},
  };
}

function normalizeNonNegativeInteger(value, fallback) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0
    ? Math.floor(parsedValue)
    : fallback;
}

function normalizePercent(value, fallback) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue)
    ? Math.min(100, Math.max(0, parsedValue))
    : fallback;
}

function normalizePositiveDecimal(value, fallback) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
}

function parseDamageSplitByCategory(value) {
  if (!value) {
    return {};
  }

  let parsedValue = value;
  if (typeof parsedValue === "string") {
    try {
      parsedValue = JSON.parse(parsedValue);
    } catch (error) {
      return {};
    }
  }

  if (
    !parsedValue ||
    typeof parsedValue !== "object" ||
    Array.isArray(parsedValue)
  ) {
    return {};
  }

  return Object.entries(parsedValue).reduce((result, [category, row]) => {
    if (!category || !row || typeof row !== "object") {
      return result;
    }

    const nextRow = {
      A: Number(row.A),
      B: Number(row.B),
      C: Number(row.C),
    };

    if (Object.values(nextRow).some((entry) => Number.isNaN(entry))) {
      return result;
    }

    result[category] = nextRow;
    return result;
  }, {});
}

function validateDamageSplitByCategory(value) {
  const entries = Object.entries(value || {});
  if (entries.length === 0) {
    return null;
  }

  const invalidCategories = entries
    .filter(([, row]) => {
      const total =
        (Number(row?.A) || 0) + (Number(row?.B) || 0) + (Number(row?.C) || 0);
      return Math.abs(total - 100) > 0.001;
    })
    .map(([category]) => category);

  if (invalidCategories.length === 0) {
    return null;
  }

  return `Damage split must total 100% per category. Check: ${invalidCategories.join(", ")}`;
}

const CreateRepairProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { withLoading } = useCustomLoading();
  const isEditMode = Boolean(id);
  const currentYear = new Date().getFullYear();

  const [bridges, setBridges] = useState([]);
  const [selectedSpanIds, setSelectedSpanIds] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState(null);
  const [estimatedCosts, setEstimatedCosts] = useState(null);
  const [estimateError, setEstimateError] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [loadingProject, setLoadingProject] = useState(isEditMode);
  const [inspectionData, setInspectionData] = useState(null);
  const [areaDataLevelLabel, setAreaDataLevelLabel] = useState("Elements");
  const [weeksLimit, setWeeksLimit] = useState(FALLBACK_WEEKS_LIMIT);
  const [defaultWeeksLimit, setDefaultWeeksLimit] =
    useState(FALLBACK_WEEKS_LIMIT);
  const [defaultLayersPerDay, setDefaultLayersPerDay] = useState(
    DEFAULT_SCENARIO.layersPerDay,
  );
  const [defaultSystemCurrency, setDefaultSystemCurrency] = useState("CAD");
  const [selectedMobilizationConfigId, setSelectedMobilizationConfigId] =
    useState(null);
  const [scenario, setScenario] = useState(createDefaultScenario);
  const [damageSplitDefaultsFromSettings, setDamageSplitDefaultsFromSettings] =
    useState([]);
  const [isScenarioConfigCollapsed, setIsScenarioConfigCollapsed] =
    useState(true);
  const [mobilizationConfigVersion, setMobilizationConfigVersion] = useState(0);
  const [generalCostCatalogVersion, setGeneralCostCatalogVersion] = useState(0);
  const [showGeneralCostDrawer, setShowGeneralCostDrawer] = useState(false);
  const [savingGeneralCost, setSavingGeneralCost] = useState(false);
  const [pendingGeneralCostToggleIds, setPendingGeneralCostToggleIds] =
    useState(() => new Set());
  const [selectedGeneralCostIdsOverride, setSelectedGeneralCostIdsOverride] =
    useState(null);
  const [generalCostForm, setGeneralCostForm] = useState(
    createEmptyGeneralCostForm(currentYear),
  );
  const [generalCostYearOptions, setGeneralCostYearOptions] = useState([]);
  const [exposureClassOptions, setExposureClassOptions] = useState([]);
  const [generalCostConversionRates, setGeneralCostConversionRates] = useState(
    {},
  );
  const hasScenarioOverrideRef = useRef(false);
  const hasPrefilledDamageSplitRef = useRef(false);

  const [formData, setFormData] = useState({
    bridgeId: "",
    projectName: "",
    projectCode: "",
    description: "",
    plannedYear: new Date().getFullYear(),
    plannedStartDate: "",
    plannedEndDate: "",
    priority: "medium",
    useInspectionData: false,
    notes: "",
  });

  const fetchBridges = useCallback(async () => {
    try {
      const response = await api.get("/bridges?limit=100");
      if (response.data.success) {
        setBridges(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching bridges:", err);
      setError("Failed to fetch bridges");
    }
  }, []);

  const fetchPlanningDefaults = useCallback(async () => {
    try {
      const response = await api.get("/settings/categories");
      if (response.data.success) {
        const projectDefaults = response.data.data?.project_defaults || {};
        const configuredCurrency =
          response.data.data?.currency_units?.currency?.value ||
          response.data.data?.currency_units?.currency?.setting_value ||
          "CAD";
        const configuredWeeksLimit =
          projectDefaults?.default_mobilization_weeks_limit?.value;
        const configuredForemansCount =
          projectDefaults?.default_foremans_count?.value;
        const estimatedAreaRaw =
          projectDefaults?.estimated_painted_area_pct?.value;
        const configuredLayersPerDay =
          projectDefaults?.default_layers_per_day?.value;
        const normalizedDefault = normalizeWeeksLimit(configuredWeeksLimit);
        setDefaultSystemCurrency(configuredCurrency);
        setDefaultWeeksLimit(normalizedDefault);
        setDefaultLayersPerDay(
          normalizePositiveDecimal(
            configuredLayersPerDay,
            DEFAULT_SCENARIO.layersPerDay,
          ),
        );
        setWeeksLimit((previousValue) =>
          normalizeWeeksLimit(previousValue, normalizedDefault),
        );

        if (!hasScenarioOverrideRef.current) {
          setScenario((previousValue) => ({
            ...previousValue,
            estimatedPaintedAreaPercent: normalizePercent(
              estimatedAreaRaw,
              previousValue.estimatedPaintedAreaPercent,
            ),
            foremansCountLimit: normalizeNonNegativeInteger(
              configuredForemansCount,
              previousValue.foremansCountLimit,
            ),
            layersPerDay: normalizePositiveDecimal(
              configuredLayersPerDay,
              previousValue.layersPerDay,
            ),
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching create-project defaults:", err);
      setDefaultSystemCurrency("CAD");
      setDefaultWeeksLimit(FALLBACK_WEEKS_LIMIT);
      setDefaultLayersPerDay(DEFAULT_SCENARIO.layersPerDay);
      setWeeksLimit((previousValue) =>
        normalizeWeeksLimit(previousValue, FALLBACK_WEEKS_LIMIT),
      );
    }
  }, []);

  useEffect(() => {
    fetchBridges();
    fetchPlanningDefaults();
  }, [fetchBridges, fetchPlanningDefaults]);

  useEffect(() => {
    const fetchDamageSplitDefaults = async () => {
      try {
        const response = await api.get("/damage-splits?is_active=true");
        if (
          response.data?.success &&
          Array.isArray(response.data.data) &&
          response.data.data.length > 0
        ) {
          const defaults = response.data.data;
          setDamageSplitDefaultsFromSettings(defaults);

          if (
            !hasPrefilledDamageSplitRef.current &&
            !hasScenarioOverrideRef.current
          ) {
            const nextByCategory = {};
            defaults.forEach((entry) => {
              const category =
                entry.corrosionCategory || entry.corrosion_category;
              if (!category || nextByCategory[category]) {
                return;
              }

              nextByCategory[category] = {
                A: Number(entry.severityA) || 55,
                B: Number(entry.severityB) || 35,
                C: Number(entry.severityC) || 10,
              };
            });

            if (Object.keys(nextByCategory).length > 0) {
              hasPrefilledDamageSplitRef.current = true;
              setScenario((previousValue) => ({
                ...previousValue,
                damageSplitByCategory: nextByCategory,
              }));
            }
          }
        }
      } catch (err) {
        console.error("Error fetching damage split defaults:", err);
      }
    };

    fetchDamageSplitDefaults();
  }, []);

  const loadExistingProject = useCallback(async () => {
    if (!isEditMode || !id) {
      setLoadingProject(false);
      return;
    }

    try {
      setLoadingProject(true);
      setError(null);

      const response = await api.get(`/repair-projects/${id}`);
      if (!response.data.success) {
        throw new Error("Failed to load repair project");
      }

      const project = response.data.data;
      const elementIds = Array.isArray(project.elements)
        ? project.elements
            .map((element) => Number(element.element_id || element.id))
            .filter((elementId) => Number.isInteger(elementId) && elementId > 0)
        : [];

      setFormData((previousValue) => ({
        ...previousValue,
        bridgeId: project.bridge_id ? String(project.bridge_id) : "",
        projectName: project.project_name || "",
        projectCode: project.project_code || "",
        description: project.description || "",
        plannedYear: Number(project.planned_year) || previousValue.plannedYear,
        plannedStartDate: project.planned_start_date
          ? String(project.planned_start_date).slice(0, 10)
          : "",
        plannedEndDate: project.planned_end_date
          ? String(project.planned_end_date).slice(0, 10)
          : "",
        priority: project.priority || "medium",
        useInspectionData: Boolean(project.uses_inspection_data),
        notes: project.notes || "",
      }));
      setWeeksLimit(
        normalizeWeeksLimit(project.weeks_limit, FALLBACK_WEEKS_LIMIT),
      );
      setSelectedMobilizationConfigId(
        Number(project.mobilization_config_id || 0) || null,
      );

      const storedDamageSplitByCategory = parseDamageSplitByCategory(
        project.scenario_damage_split_by_category,
      );
      const storedDamageAllocationMode =
        project.scenario_damage_allocation_mode ===
        DAMAGE_ALLOCATION_MODES.DAMAGE_SPLIT
          ? DAMAGE_ALLOCATION_MODES.DAMAGE_SPLIT
          : project.scenario_damage_allocation_mode ===
              DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL
            ? DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL
            : null;
      const hasStoredScenario =
        project.scenario_estimated_painted_area_pct != null ||
        project.scenario_foremans_count_limit != null ||
        project.scenario_layers_per_day != null ||
        storedDamageAllocationMode != null ||
        Object.keys(storedDamageSplitByCategory).length > 0;

      if (hasStoredScenario) {
        hasScenarioOverrideRef.current = true;
        hasPrefilledDamageSplitRef.current = true;
        setScenario((previousValue) => ({
          ...previousValue,
          damageAllocationMode:
            storedDamageAllocationMode || previousValue.damageAllocationMode,
          estimatedPaintedAreaPercent: normalizePercent(
            project.scenario_estimated_painted_area_pct,
            previousValue.estimatedPaintedAreaPercent,
          ),
          foremansCountLimit: normalizeNonNegativeInteger(
            project.scenario_foremans_count_limit,
            previousValue.foremansCountLimit,
          ),
          layersPerDay: normalizePositiveDecimal(
            project.scenario_layers_per_day,
            previousValue.layersPerDay,
          ),
          damageSplitByCategory:
            Object.keys(storedDamageSplitByCategory).length > 0
              ? storedDamageSplitByCategory
              : previousValue.damageSplitByCategory,
        }));
      }

      setSelectedSpanIds(elementIds);
      setInspectionData(null);
      setEstimatedCosts(null);
      setEstimateError(null);
      setCurrentStep(1);
    } catch (err) {
      console.error("Error loading repair project:", err);
      setError(err.response?.data?.message || "Failed to load repair project");
    } finally {
      setLoadingProject(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    loadExistingProject();
  }, [loadExistingProject]);

  useEffect(() => {
    const fetchBridgeLevelConfig = async () => {
      if (!formData.bridgeId) {
        setAreaDataLevelLabel("Elements");
        return;
      }

      try {
        const response = await api.get(`/bridges/${formData.bridgeId}`);
        if (response.data.success) {
          const bridge = response.data.data;
          const levelLabels = bridge.levelLabels || {};

          let areaLevelLabel = "Elements";
          for (const levelConfig of Object.values(levelLabels)) {
            if (levelConfig?.holdsAreaData) {
              areaLevelLabel = levelConfig.label || "Elements";
              break;
            }
          }

          setAreaDataLevelLabel(areaLevelLabel);
        }
      } catch (err) {
        console.error("Error fetching bridge level config:", err);
      }
    };

    fetchBridgeLevelConfig();
  }, [formData.bridgeId]);

  useEffect(() => {
    if (!formData.bridgeId) {
      setEstimatedCosts(null);
      setEstimateError(null);
    }
  }, [formData.bridgeId]);

  const currentSelectedConfigYear = Number(
    estimatedCosts?.summary?.configSummary?.configYear || 0,
  );

  const exposureCodeById = useMemo(() => {
    const map = new Map();
    (exposureClassOptions || []).forEach((row) => {
      map.set(Number(row.id), row.categoryCode || row.category_code);
    });
    return map;
  }, [exposureClassOptions]);

  const normalizeExposureIds = useCallback((ids) => {
    if (!Array.isArray(ids)) return [];
    return ids
      .map((value) => parseInt(value, 10))
      .filter((value) => Number.isInteger(value) && value > 0);
  }, []);

  const generalCostFallbackYearOptions = useMemo(() => {
    const years = new Set([currentYear, currentYear + 1]);
    const plannedYear = Number(formData.plannedYear || 0);
    const formYear = Number(generalCostForm.year || 0);

    if (plannedYear > 0) {
      years.add(plannedYear);
    }
    if (currentSelectedConfigYear > 0) {
      years.add(currentSelectedConfigYear);
    }
    if (formYear > 0) {
      years.add(formYear);
    }

    return Array.from(years).sort((left, right) => right - left);
  }, [
    currentSelectedConfigYear,
    currentYear,
    formData.plannedYear,
    generalCostForm.year,
  ]);

  const fetchRepairProjectGeneralCostOptions = useCallback(async () => {
    try {
      const [exposureOptionsResponse, generalCostsResponse] = await Promise.all(
        [
          api.get(
            "/corrosion-damage-combinations/options/corrosion-categories",
          ),
          api.get("/mobilization-master/costs?grouped_by_year=true"),
        ],
      );

      setExposureClassOptions(exposureOptionsResponse.data?.data || []);

      const years = new Set(generalCostFallbackYearOptions);
      (generalCostsResponse.data?.data || []).forEach((group) => {
        const groupYear = Number(group.year);
        if (Number.isFinite(groupYear) && groupYear > 0) {
          years.add(groupYear);
        }
      });

      setGeneralCostYearOptions(
        Array.from(years).sort((left, right) => right - left),
      );
    } catch (err) {
      console.error("Error fetching repair-project general cost options:", err);
      setExposureClassOptions([]);
      setGeneralCostYearOptions(generalCostFallbackYearOptions);
    }
  }, [generalCostFallbackYearOptions]);

  const openGeneralCostDrawer = useCallback(() => {
    const baseYear =
      currentSelectedConfigYear > 0
        ? currentSelectedConfigYear
        : Number(formData.plannedYear || currentYear) || currentYear;

    setGeneralCostForm(
      createEmptyGeneralCostForm(baseYear, defaultSystemCurrency),
    );
    setGeneralCostYearOptions([]);
    setShowGeneralCostDrawer(true);
  }, [
    currentSelectedConfigYear,
    currentYear,
    defaultSystemCurrency,
    formData.plannedYear,
  ]);

  const closeGeneralCostDrawer = useCallback(() => {
    if (savingGeneralCost) {
      return;
    }

    setShowGeneralCostDrawer(false);
  }, [savingGeneralCost]);

  useEffect(() => {
    if (!showGeneralCostDrawer) {
      return;
    }

    fetchRepairProjectGeneralCostOptions();
  }, [fetchRepairProjectGeneralCostOptions, showGeneralCostDrawer]);

  const getGeneralCostConversionRate = useCallback(
    (data) => {
      const year = Number(data.year || currentYear);
      const currency = String(data.currency || defaultSystemCurrency);

      if (!currency || currency === defaultSystemCurrency) {
        return 1;
      }

      const key = `${year}|${currency}|${defaultSystemCurrency}`;
      const rate = generalCostConversionRates[key];
      return Number(rate || 0) > 0 ? Number(rate) : null;
    },
    [currentYear, defaultSystemCurrency, generalCostConversionRates],
  );

  useEffect(() => {
    if (!showGeneralCostDrawer) {
      return;
    }

    const year = Number(generalCostForm.year || currentYear);
    const currency = String(generalCostForm.currency || defaultSystemCurrency);

    if (!currency || currency === defaultSystemCurrency) {
      return;
    }

    const key = `${year}|${currency}|${defaultSystemCurrency}`;
    if (generalCostConversionRates[key] !== undefined) {
      return;
    }

    let cancelled = false;

    const fetchConversionRate = async () => {
      try {
        const response = await api.get(
          `/currency-conversion-rates/year/${year}/${currency}/${defaultSystemCurrency}`,
        );
        const rate = Number(
          response.data?.data?.conversionRate || response.data?.data?.rate || 0,
        );

        if (!cancelled) {
          setGeneralCostConversionRates((previousValue) => ({
            ...previousValue,
            [key]: rate > 0 ? rate : null,
          }));
        }
      } catch (err) {
        console.error(
          "Error fetching repair-project general cost conversion rate:",
          err,
        );

        if (!cancelled) {
          setGeneralCostConversionRates((previousValue) => ({
            ...previousValue,
            [key]: null,
          }));
        }
      }
    };

    fetchConversionRate();

    return () => {
      cancelled = true;
    };
  }, [
    currentYear,
    defaultSystemCurrency,
    generalCostConversionRates,
    generalCostForm.currency,
    generalCostForm.year,
    showGeneralCostDrawer,
  ]);

  const calculateGeneralCostTotal = useCallback(
    (data, useSystemCurrency = true) => {
      const total = calculateGeneralCostBaseTotal({
        data,
        weekCountForPreview: Number(data.week_count || data.weekCount),
      });

      if (!useSystemCurrency) {
        return total;
      }

      const conversionRate = getGeneralCostConversionRate(data);
      if (conversionRate === null) {
        return null;
      }

      return total * conversionRate;
    },
    [getGeneralCostConversionRate],
  );

  const handleSaveGeneralCost = async () => {
    if (!generalCostForm.cost_category.trim()) {
      alert("Cost name is required.");
      return;
    }

    setSavingGeneralCost(true);
    let createdCostId = null;

    try {
      const createResponse = await api.post("/mobilization-master/costs", {
        year: Number(generalCostForm.year),
        cost_category: generalCostForm.cost_category.trim(),
        description: generalCostForm.description?.trim() || null,
        calculation_type: generalCostForm.calculation_type,
        confined_space: Boolean(generalCostForm.confined_space),
        quantity_scales_with_teams: Boolean(
          generalCostForm.quantity_scales_with_teams,
        ),
        quantity: Number(generalCostForm.quantity || 0),
        rate: Number(generalCostForm.rate || 0),
        rate_per_week: Number(generalCostForm.rate_per_week || 0),
        hours_per_day: Number(generalCostForm.hours_per_day || 0),
        day_count: Number(generalCostForm.day_count || 0),
        movement_frequency_days: Number(
          generalCostForm.movement_frequency_days || 0,
        ),
        week_count: Number(generalCostForm.week_count || 0),
        exposure_class_ids: normalizeExposureIds(
          generalCostForm.exposure_class_ids,
        ),
        sort_order: Number(generalCostForm.sort_order || 99),
        currency: generalCostForm.currency || defaultSystemCurrency,
        cost_per_unit: Number(generalCostForm.rate || 0),
        unit_type: "custom",
      });

      createdCostId = Number(createResponse.data?.data?.id || 0) || null;

      const targetConfigId =
        Number(
          selectedMobilizationConfigId ||
            estimatedCosts?.summary?.configSummary?.configId ||
            0,
        ) || null;

      if (targetConfigId && createdCostId) {
        const configsResponse = await api.get(
          "/mobilization-master/mobilization-configs?grouped_by_year=true",
        );
        const configGroups = configsResponse.data?.data || [];
        const targetConfig = configGroups
          .flatMap((group) => group.configs || [])
          .find((row) => Number(row.id) === targetConfigId);

        if (targetConfig) {
          const existingSelectedIds = normalizeExposureIds(
            targetConfig.selectedGeneralCostIds ||
              targetConfig.selected_general_cost_ids ||
              [],
          );

          if (
            existingSelectedIds.length > 0 &&
            !existingSelectedIds.includes(createdCostId)
          ) {
            await api.put(
              `/mobilization-master/mobilization-configs/${targetConfigId}`,
              {
                selected_general_cost_ids: [
                  ...existingSelectedIds,
                  createdCostId,
                ],
              },
            );
          }
        }
      }

      setShowGeneralCostDrawer(false);
      setGeneralCostCatalogVersion((previousValue) => previousValue + 1);
      setMobilizationConfigVersion((previousValue) => previousValue + 1);
    } catch (err) {
      if (createdCostId) {
        setShowGeneralCostDrawer(false);
        setGeneralCostCatalogVersion((previousValue) => previousValue + 1);
        setMobilizationConfigVersion((previousValue) => previousValue + 1);
        alert(
          "General cost was created, but it could not be linked to the selected mobilization configuration automatically. You can add it manually from Teams & Productivity Settings.",
        );
      } else {
        alert(err.response?.data?.message || "Failed to save general cost.");
      }
    } finally {
      setSavingGeneralCost(false);
    }
  };

  const fetchInspectionData = useCallback(async (spanIds) => {
    try {
      const response = await api.get(
        `/repair-projects/analyze?element_ids=${spanIds.join(",")}`,
      );
      if (response.data.success) {
        setInspectionData(response.data.data);
      }
    } catch (fetchError) {
      console.log("Inspection data not available:", fetchError.message);
      setInspectionData(null);
    }
  }, []);

  const handleSpanSelection = useCallback((spanIds) => {
    const nextSpanIds = Array.isArray(spanIds) ? spanIds : [];
    setSelectedSpanIds((previousIds) =>
      areIdListsEqual(previousIds, nextSpanIds) ? previousIds : nextSpanIds,
    );
  }, []);

  useEffect(() => {
    if (selectedSpanIds.length === 0) {
      setInspectionData(null);
      return;
    }

    fetchInspectionData(selectedSpanIds);
  }, [fetchInspectionData, selectedSpanIds]);

  const handleInputChange = (field, value) => {
    setFormData((previousValue) => ({
      ...previousValue,
      [field]: value,
    }));
  };

  const handleScenarioChange = useCallback((field, value) => {
    hasScenarioOverrideRef.current = true;
    setScenario((previousValue) => ({
      ...previousValue,
      [field]: value,
    }));
  }, []);

  const handleDamageSplitByCategoryChange = useCallback(
    (category, level, value) => {
      const parsedValue = parseInt(value, 10);
      if (Number.isNaN(parsedValue)) {
        return;
      }

      hasScenarioOverrideRef.current = true;
      hasPrefilledDamageSplitRef.current = true;
      setScenario((previousValue) => {
        const nextByCategory = {
          ...(previousValue.damageSplitByCategory || {}),
        };
        const currentRow =
          nextByCategory[category] || DEFAULT_SCENARIO.damageSplit;
        nextByCategory[category] = {
          ...currentRow,
          [level]: parsedValue,
        };
        return {
          ...previousValue,
          damageSplitByCategory: nextByCategory,
        };
      });
    },
    [],
  );

  const scenarioDamageSplitError = useMemo(
    () => validateDamageSplitByCategory(scenario.damageSplitByCategory),
    [scenario.damageSplitByCategory],
  );

  const scenarioPayload = useMemo(
    () => ({
      damageAllocationMode:
        scenario.damageAllocationMode === DAMAGE_ALLOCATION_MODES.DAMAGE_SPLIT
          ? DAMAGE_ALLOCATION_MODES.DAMAGE_SPLIT
          : DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL,
      damageSplit: { ...DEFAULT_SCENARIO.damageSplit },
      damageSplitByCategory: parseDamageSplitByCategory(
        scenario.damageSplitByCategory,
      ),
      estimatedPaintedAreaPercent: normalizePercent(
        scenario.estimatedPaintedAreaPercent,
        DEFAULT_SCENARIO.estimatedPaintedAreaPercent,
      ),
      foremansCountLimit: normalizeNonNegativeInteger(
        scenario.foremansCountLimit,
        DEFAULT_SCENARIO.foremansCountLimit,
      ),
      layersPerDay: normalizePositiveDecimal(
        scenario.layersPerDay,
        DEFAULT_SCENARIO.layersPerDay,
      ),
    }),
    [
      scenario.damageAllocationMode,
      scenario.damageSplitByCategory,
      scenario.estimatedPaintedAreaPercent,
      scenario.foremansCountLimit,
      scenario.layersPerDay,
    ],
  );

  const scenarioCategories = useMemo(
    () =>
      [
        ...new Set([
          ...(damageSplitDefaultsFromSettings || [])
            .map((entry) => entry.corrosionCategory || entry.corrosion_category)
            .filter(Boolean),
          ...Object.keys(scenario.damageSplitByCategory || {}),
        ]),
      ].sort(),
    [damageSplitDefaultsFromSettings, scenario.damageSplitByCategory],
  );

  const handleBridgeChange = (value) => {
    setFormData((previousValue) => ({
      ...previousValue,
      bridgeId: value,
    }));
    setSelectedSpanIds([]);
    setInspectionData(null);
    setEstimatedCosts(null);
    setEstimateError(null);
  };

  useEffect(() => {
    const fetchEstimatedCosts = async () => {
      if (!formData.bridgeId || selectedSpanIds.length === 0) {
        setEstimatedCosts(null);
        setEstimateError(null);
        setEstimating(false);
        return;
      }

      if (scenarioDamageSplitError) {
        setEstimatedCosts(null);
        setEstimateError(scenarioDamageSplitError);
        setEstimating(false);
        return;
      }

      try {
        setEstimating(true);
        const response = await api.post("/repair-projects/estimate", {
          bridgeId: formData.bridgeId,
          plannedYear: formData.plannedYear,
          weeksLimit: parseWeekLimit(weeksLimit),
          mobilizationConfigId: selectedMobilizationConfigId,
          scenario: scenarioPayload,
          spanIds: selectedSpanIds,
        });

        if (response.data.success) {
          setEstimatedCosts(response.data.data);
          setEstimateError(null);
        }
      } catch (err) {
        console.error("Error estimating repair project:", err);
        setEstimatedCosts(null);
        setEstimateError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to estimate repair project costs",
        );
      } finally {
        setEstimating(false);
      }
    };

    fetchEstimatedCosts();
  }, [
    formData.bridgeId,
    formData.plannedYear,
    mobilizationConfigVersion,
    selectedMobilizationConfigId,
    selectedSpanIds,
    scenarioDamageSplitError,
    scenarioPayload,
    weeksLimit,
  ]);

  const isProjectDetailsComplete = Boolean(
    formData.bridgeId && formData.projectName.trim(),
  );
  const isElementSelectionComplete = selectedSpanIds.length > 0;
  const canOpenElementsStep = isProjectDetailsComplete;
  const canOpenCostsStep = canOpenElementsStep && isElementSelectionComplete;
  const canCreateProject = Boolean(
    canOpenCostsStep &&
    estimatedCosts?.summary &&
    !estimateError &&
    !estimating &&
    !scenarioDamageSplitError &&
    parseWeekLimit(weeksLimit),
  );
  const hasExistingEstimateSummary = Boolean(estimatedCosts?.summary);
  const showEstimateLoadingBanner = estimating && !hasExistingEstimateSummary;
  const showInlineEstimateRefresh = estimating && hasExistingEstimateSummary;

  useEffect(() => {
    if (currentStep === 3 && !canOpenCostsStep) {
      setCurrentStep(canOpenElementsStep ? 2 : 1);
      return;
    }

    if (currentStep === 2 && !canOpenElementsStep) {
      setCurrentStep(1);
    }
  }, [canOpenCostsStep, canOpenElementsStep, currentStep]);

  const handleStepChange = (stepId) => {
    if (stepId === 1) {
      setCurrentStep(1);
      return;
    }

    if (stepId === 2 && canOpenElementsStep) {
      setCurrentStep(2);
      return;
    }

    if (stepId === 3 && canOpenCostsStep) {
      setCurrentStep(3);
    }
  };

  const handleContinueFromDetails = () => {
    if (!formData.bridgeId) {
      setError("Please select a bridge");
      return;
    }

    if (!formData.projectName.trim()) {
      setError("Please enter a project name");
      return;
    }

    setError(null);
    setCurrentStep(2);
  };

  const handleContinueFromElements = () => {
    if (selectedSpanIds.length === 0) {
      setError(
        `Please select at least one ${areaDataLevelLabel.toLowerCase()}`,
      );
      return;
    }

    setError(null);
    setCurrentStep(3);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!formData.bridgeId) {
      setCurrentStep(1);
      setError("Please select a bridge");
      return;
    }

    if (!formData.projectName.trim()) {
      setCurrentStep(1);
      setError("Please enter a project name");
      return;
    }

    if (selectedSpanIds.length === 0) {
      setCurrentStep(2);
      setError(
        `Please select at least one ${areaDataLevelLabel.toLowerCase()}`,
      );
      return;
    }

    if (!parseWeekLimit(weeksLimit)) {
      setCurrentStep(3);
      setError("Please provide a valid weeks limit");
      return;
    }

    if (scenarioDamageSplitError) {
      setCurrentStep(3);
      setError(scenarioDamageSplitError);
      return;
    }

    try {
      await withLoading(
        async () => {
          const payload = {
            ...formData,
            weeksLimit: parseWeekLimit(weeksLimit),
            mobilizationConfigId: selectedMobilizationConfigId,
            scenario: scenarioPayload,
            spanIds: selectedSpanIds,
          };
          const response = isEditMode
            ? await api.put(`/repair-projects/${id}`, payload)
            : await api.post("/repair-projects", payload);

          if (response.data.success) {
            navigate(
              `/repair-projects/${isEditMode ? id : response.data.data.id}`,
            );
          }
        },
        isEditMode
          ? "Saving repair project changes..."
          : "Creating repair project...",
      );
    } catch (err) {
      console.error("Error saving project:", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          (isEditMode
            ? "Failed to save repair project changes"
            : "Failed to create repair project"),
      );
    }
  };

  const getStepState = (stepId) => {
    if (stepId === 1) {
      return {
        complete: isProjectDetailsComplete,
        accessible: true,
      };
    }

    if (stepId === 2) {
      return {
        complete: isElementSelectionComplete,
        accessible: canOpenElementsStep,
      };
    }

    return {
      complete: canCreateProject,
      accessible: canOpenCostsStep,
    };
  };

  const selectedTeamLabel =
    estimatedCosts?.summary?.configSummary?.teamName ||
    "Derived from active configuration";
  const effectiveSelectedMobilizationConfigId =
    Number(
      selectedMobilizationConfigId ||
        estimatedCosts?.summary?.configSummary?.configId ||
        0,
    ) || null;
  const allRepairGeneralCosts = useMemo(() => {
    if (Array.isArray(estimatedCosts?.summary?.allGeneralCostBreakdown)) {
      return estimatedCosts.summary.allGeneralCostBreakdown;
    }

    return Array.isArray(estimatedCosts?.summary?.generalCostBreakdown)
      ? estimatedCosts.summary.generalCostBreakdown.map((row) => ({
          ...row,
          isSelected: true,
        }))
      : [];
  }, [estimatedCosts]);
  const estimatedSelectedGeneralCostIds = useMemo(() => {
    const selectedIds = Array.isArray(estimatedCosts?.summary?.selectedGeneralCostIds)
      ? estimatedCosts.summary.selectedGeneralCostIds
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0)
      : [];

    if (selectedIds.length > 0) {
      return selectedIds;
    }

    return allRepairGeneralCosts
      .filter((item) => item.isSelected !== false)
      .map((item) => Number(item.id))
      .filter((value) => Number.isInteger(value) && value > 0);
  }, [allRepairGeneralCosts, estimatedCosts]);
  const effectiveSelectedGeneralCostIds = useMemo(
    () =>
      Array.isArray(selectedGeneralCostIdsOverride)
        ? selectedGeneralCostIdsOverride
        : estimatedSelectedGeneralCostIds,
    [estimatedSelectedGeneralCostIds, selectedGeneralCostIdsOverride],
  );
  const selectedGeneralCostIdSet = useMemo(
    () => new Set(effectiveSelectedGeneralCostIds),
    [effectiveSelectedGeneralCostIds],
  );
  const displayedGeneralCostsTotal = useMemo(
    () =>
      allRepairGeneralCosts.reduce((sum, item) => {
        const normalizedItemId = Number(item.id);
        const isSelected =
          normalizedItemId > 0
            ? selectedGeneralCostIdSet.has(normalizedItemId)
            : item.isSelected !== false;

        if (!isSelected) {
          return sum;
        }

        return (
          sum +
          Number(item.convertedTotal || item.total || item.amount || 0)
        );
      }, 0),
    [allRepairGeneralCosts, selectedGeneralCostIdSet],
  );
  const displayedEstimatedMobilizationCost = useMemo(() => {
    if (!estimatedCosts?.summary) {
      return 0;
    }

    return (
      Number(estimatedCosts.summary.estimatedMobilizationCost || 0) -
      Number(estimatedCosts.summary.generalCostsTotal || 0) +
      displayedGeneralCostsTotal
    );
  }, [displayedGeneralCostsTotal, estimatedCosts]);
  const displayedEstimatedTotalCost = useMemo(() => {
    if (!estimatedCosts?.summary) {
      return 0;
    }

    return (
      Number(estimatedCosts.summary.estimatedTotalCost || 0) -
      Number(estimatedCosts.summary.generalCostsTotal || 0) +
      displayedGeneralCostsTotal
    );
  }, [displayedGeneralCostsTotal, estimatedCosts]);
  const isAnyGeneralCostTogglePending = pendingGeneralCostToggleIds.size > 0;

  useEffect(() => {
    setSelectedGeneralCostIdsOverride(null);
  }, [estimatedCosts, effectiveSelectedMobilizationConfigId]);

  const handleToggleRepairGeneralCost = useCallback(
    async (costId) => {
      const normalizedCostId = Number(costId);
      if (!(normalizedCostId > 0) || !effectiveSelectedMobilizationConfigId) {
        return;
      }

      const toggleKey = String(normalizedCostId);
      const nextSelectedIds = effectiveSelectedGeneralCostIds.includes(
        normalizedCostId,
      )
        ? effectiveSelectedGeneralCostIds.filter((id) => id !== normalizedCostId)
        : [...effectiveSelectedGeneralCostIds, normalizedCostId].sort(
            (left, right) => left - right,
          );
      const previousSelectedIds = effectiveSelectedGeneralCostIds;

      setPendingGeneralCostToggleIds((prev) => {
        const next = new Set(prev);
        next.add(toggleKey);
        return next;
      });
      setSelectedGeneralCostIdsOverride(nextSelectedIds);

      try {
        await api.put(
          `/mobilization-master/mobilization-configs/${effectiveSelectedMobilizationConfigId}`,
          {
            selected_general_cost_ids: nextSelectedIds,
          },
          {
            skipGlobalLoading: true,
          },
        );
      } catch (err) {
        setSelectedGeneralCostIdsOverride(previousSelectedIds);
        alert(
          err.response?.data?.message ||
            "Failed to update general cost selection",
        );
      } finally {
        setPendingGeneralCostToggleIds((prev) => {
          const next = new Set(prev);
          next.delete(toggleKey);
          return next;
        });
      }
    },
    [effectiveSelectedGeneralCostIds, effectiveSelectedMobilizationConfigId],
  );

  const currentStepConfig =
    STEPS.find((step) => step.id === currentStep) || STEPS[0];
  const actionLabel = isEditMode ? "Save Changes" : "Create Project";
  const pageTitle = isEditMode
    ? "Edit Repair Project"
    : "Create Repair Project";
  const backTarget = isEditMode ? `/repair-projects/${id}` : "/repair-projects";
  const backLabel = isEditMode
    ? "Back to Project Details"
    : "Back to Repair Projects";
  const plannedYearMin = Math.min(
    currentYear,
    Number(formData.plannedYear) || currentYear,
  );
  const plannedYearMax = Math.max(
    currentYear + 10,
    Number(formData.plannedYear) || currentYear,
  );
  const inspectionAvailableCount = Array.isArray(inspectionData?.elements)
    ? inspectionData.elements.filter(
        (element) => element.inspection?.hasInspectionData,
      ).length
    : Array.isArray(inspectionData?.spans)
      ? inspectionData.spans.filter((span) => span.inspection).length
      : 0;

  if (loadingProject) {
    return (
      <div className="flex min-h-[24rem] items-center justify-center rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="text-center text-slate-600">
          <FontAwesomeIcon icon={faSpinner} spin className="text-3xl" />
          <p className="mt-3 text-sm font-medium">
            Loading repair project data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 py-3 sm:py-4">
      <form
        onSubmit={handleSubmit}
        className="grid w-full gap-3 lg:grid-cols-[18rem_minmax(0,_1fr)] lg:items-start xl:grid-cols-[19rem_minmax(0,_1fr)]"
      >
        <aside className="h-fit rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-4 lg:self-start">
          <button
            type="button"
            onClick={() => navigate(backTarget)}
            className="flex w-full items-center gap-2 rounded-2xl px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>{backLabel}</span>
          </button>

          <div className="mt-6">
            {STEPS.map((step, index) => {
              const stepState = getStepState(step.id);
              const isActive = currentStep === step.id;

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => handleStepChange(step.id)}
                  disabled={!stepState.accessible}
                  className="group relative flex w-full items-start gap-4 pb-6 text-left last:pb-0 disabled:cursor-not-allowed"
                >
                  {index < STEPS.length - 1 && (
                    <span className="absolute left-[1.35rem] top-12 h-[calc(100%-1rem)] w-px bg-slate-200" />
                  )}
                  <span
                    className={`relative z-10 flex h-11 w-11 items-center justify-center rounded-full border-2 text-sm font-semibold transition ${
                      isActive
                        ? "border-blue-500 bg-blue-500 text-white shadow-lg shadow-blue-100"
                        : stepState.complete
                          ? "border-emerald-500 bg-emerald-500 text-white"
                          : "border-slate-200 bg-slate-100 text-slate-500"
                    }`}
                  >
                    {stepState.complete && !isActive ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : (
                      step.id
                    )}
                  </span>
                  <span className="pt-1">
                    <span
                      className={`block text-sm font-semibold ${
                        isActive ? "text-slate-900" : "text-slate-700"
                      }`}
                    >
                      {step.title}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">
                      {step.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            <div className="flex items-center justify-between gap-4">
              <span>Selected {areaDataLevelLabel}</span>
              <span className="font-semibold text-slate-900">
                {selectedSpanIds.length}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-4">
              <span>Weeks Limit</span>
              <span className="font-semibold text-slate-900">
                {normalizeWeeksLimit(weeksLimit, defaultWeeksLimit)}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-4">
              <span>Derived Teams</span>
              <span className="font-semibold text-slate-900">
                {estimatedCosts?.summary?.teamsRequired || 0}
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={!canCreateProject || currentStep !== 3}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-100 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
          >
            <FontAwesomeIcon icon={faSave} />
            <span>{actionLabel}</span>
          </button>
          <p className="mt-2 text-xs leading-5 text-slate-500">
            {isEditMode
              ? "Review costs before saving changes."
              : "Review costs before creating the project."}
          </p>
        </aside>

        <div className="min-w-0 space-y-6">
          <div className="sticky top-0 z-30 bg-slate-50/95 pb-2 backdrop-blur supports-[backdrop-filter]:bg-slate-50/85">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,_1fr)_auto_auto] lg:items-center">
                <div className="min-w-0">
                  <h1 className="text-3xl font-bold text-slate-900">
                    {pageTitle}
                  </h1>
                  <p className="mt-2 text-sm font-semibold text-slate-700">
                    {currentStepConfig.id}. {currentStepConfig.title}
                  </p>
                </div>

                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 lg:justify-self-end"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>
                      {currentStep === 2
                        ? "Back to Details"
                        : "Back to Elements"}
                    </span>
                  </button>
                ) : (
                  <div className="hidden lg:block" />
                )}

                {currentStep === 1 && (
                  <button
                    type="button"
                    onClick={handleContinueFromDetails}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 lg:justify-self-end"
                  >
                    <span>Continue to Elements</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                )}

                {currentStep === 2 && (
                  <button
                    type="button"
                    onClick={handleContinueFromElements}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 lg:justify-self-end"
                  >
                    <span>Continue to Costs</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                )}

                {currentStep === 3 && (
                  <button
                    type="submit"
                    disabled={!canCreateProject}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-100 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none lg:justify-self-end"
                  >
                    <FontAwesomeIcon icon={faSave} />
                    <span>{actionLabel}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
              {error}
            </div>
          )}

          {currentStep === 1 && (
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Bridge <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.bridgeId}
                    onChange={(event) => handleBridgeChange(event.target.value)}
                    required
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="">Select a bridge</option>
                    {bridges.map((bridge) => (
                      <option key={bridge.id} value={bridge.id}>
                        {bridge.bridgeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.projectName}
                    onChange={(event) =>
                      handleInputChange("projectName", event.target.value)
                    }
                    required
                    placeholder="e.g., 2026 Northbound Repair"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Project Code
                  </label>
                  <input
                    type="text"
                    value={formData.projectCode}
                    onChange={(event) =>
                      handleInputChange("projectCode", event.target.value)
                    }
                    placeholder="Optional unique code"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Planned Year
                  </label>
                  <input
                    type="number"
                    value={formData.plannedYear}
                    onChange={(event) =>
                      handleInputChange(
                        "plannedYear",
                        parseInt(event.target.value, 10),
                      )
                    }
                    min={plannedYearMin}
                    max={plannedYearMax}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(event) =>
                      handleInputChange("priority", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(event) =>
                      handleInputChange("description", event.target.value)
                    }
                    rows={4}
                    placeholder="Project description..."
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>
            </section>
          )}

          {currentStep === 2 && (
            <section className="space-y-4">
              {formData.bridgeId ? (
                <>
                  <BridgeSelectionManager
                    bridgeId={formData.bridgeId}
                    initialSelectedIds={selectedSpanIds}
                    onSelectionChange={handleSpanSelection}
                    title="Select Elements"
                    required
                    emptyMessage="Add one or more bridge selections to choose the repair scope."
                    containerClassName="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
                  />

                  {inspectionData &&
                    selectedSpanIds.length > 0 &&
                    inspectionAvailableCount > 0 && (
                      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
                        <h4 className="text-sm font-semibold text-emerald-800">
                          <FontAwesomeIcon icon={faEye} className="mr-2" />
                          Inspection Data Available
                        </h4>
                        <div className="mt-2 text-sm text-emerald-700">
                          <p>
                            Inspection data is available for{" "}
                            {inspectionAvailableCount} of{" "}
                            {selectedSpanIds.length} selected{" "}
                            {areaDataLevelLabel.toLowerCase()}.
                          </p>
                          <p className="mt-1">
                            Repair process lookup uses stored project condition
                            data, with inspection context shown when available.
                          </p>
                        </div>
                      </div>
                    )}
                </>
              ) : (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
                  Complete Project Details first to load the hierarchy selector.
                </div>
              )}
            </section>
          )}

          {currentStep === 3 && (
            <section className="space-y-6">
              {showEstimateLoadingBanner && (
                <div className="flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 shadow-sm">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Recalculating repair project costs...</span>
                </div>
              )}

              {estimateError && !estimatedCosts && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                  {estimateError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,_36rem)_minmax(0,_1fr)]">
                <RepairProjectMobilizationConfigPanel
                  selectedConfigId={effectiveSelectedMobilizationConfigId}
                  onSelectConfigId={setSelectedMobilizationConfigId}
                  onConfigDataChanged={() =>
                    setMobilizationConfigVersion(
                      (previousValue) => previousValue + 1,
                    )
                  }
                  refreshKey={generalCostCatalogVersion}
                />

                <div className="space-y-4">
                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-slate-500 transition hover:text-slate-700"
                          onClick={() =>
                            setIsScenarioConfigCollapsed(
                              (previousValue) => !previousValue,
                            )
                          }
                          title={
                            isScenarioConfigCollapsed
                              ? "Expand scenario configuration"
                              : "Collapse scenario configuration"
                          }
                        >
                          <FontAwesomeIcon
                            icon={
                              isScenarioConfigCollapsed
                                ? faChevronRight
                                : faChevronDown
                            }
                          />
                        </button>
                        <h3 className="text-xl font-semibold text-slate-900">
                          3. Configure Scenario
                        </h3>
                      </div>
                    </div>

                    {!isScenarioConfigCollapsed && (
                      <>
                        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                              Estimated Painted Area (%)
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step="0.1"
                              value={scenario.estimatedPaintedAreaPercent}
                              onChange={(event) =>
                                handleScenarioChange(
                                  "estimatedPaintedAreaPercent",
                                  normalizePercent(
                                    event.target.value,
                                    DEFAULT_SCENARIO.estimatedPaintedAreaPercent,
                                  ),
                                )
                              }
                              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            />
                          </div>

                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                              # Foremen
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              value={scenario.foremansCountLimit}
                              onChange={(event) =>
                                handleScenarioChange(
                                  "foremansCountLimit",
                                  normalizeNonNegativeInteger(
                                    event.target.value,
                                    DEFAULT_SCENARIO.foremansCountLimit,
                                  ),
                                )
                              }
                              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            />
                          </div>
                        </div>

                        <div className="mt-6">
                          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <h4 className="text-base font-medium text-slate-900">
                              Damage Allocation
                            </h4>
                            <div className="inline-flex rounded-md border border-slate-300 bg-white p-1 shadow-sm">
                              <button
                                type="button"
                                aria-pressed={!isDamageSplitMode(scenario)}
                                onClick={() =>
                                  handleScenarioChange(
                                    "damageAllocationMode",
                                    DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL,
                                  )
                                }
                                className={`px-3 py-1.5 text-sm font-medium rounded ${
                                  !isDamageSplitMode(scenario)
                                    ? "bg-blue-600 text-white shadow-sm"
                                    : "text-slate-700 hover:bg-slate-50"
                                }`}
                              >
                                Element Damage Level
                              </button>
                              <button
                                type="button"
                                aria-pressed={isDamageSplitMode(scenario)}
                                onClick={() =>
                                  handleScenarioChange(
                                    "damageAllocationMode",
                                    DAMAGE_ALLOCATION_MODES.DAMAGE_SPLIT,
                                  )
                                }
                                className={`px-3 py-1.5 text-sm font-medium rounded ${
                                  isDamageSplitMode(scenario)
                                    ? "bg-blue-600 text-white shadow-sm"
                                    : "text-slate-700 hover:bg-slate-50"
                                }`}
                              >
                                Damage Split %
                              </button>
                            </div>
                          </div>

                          {!isDamageSplitMode(scenario) && (
                            <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                              Each element uses its own stored corrosion/damage
                              combination(s). An element stored as{" "}
                              <span className="font-mono text-xs">C5B</span> puts
                              100% of its repair area on B. An element split
                              across combinations — for example{" "}
                              <span className="font-mono text-xs">
                                C2A-60;C3B-40
                              </span>{" "}
                              — divides its area accordingly and costs each part
                              against its own combination.
                            </div>
                          )}

                          {isDamageSplitMode(scenario) ? (
                            scenarioCategories.length > 0 ? (
                            <div className="overflow-x-auto rounded-2xl border border-slate-200">
                              <table className="min-w-full table-fixed bg-white">
                                <thead>
                                  <tr className="bg-slate-50">
                                    <th className="border-b px-3 py-2 text-left text-sm font-medium text-slate-700">
                                      Category
                                    </th>
                                    <th className="w-32 border-b px-3 py-2 text-center text-sm font-medium text-slate-700">
                                      Severity A %
                                    </th>
                                    <th className="w-32 border-b px-3 py-2 text-center text-sm font-medium text-slate-700">
                                      Severity B %
                                    </th>
                                    <th className="w-32 border-b px-3 py-2 text-center text-sm font-medium text-slate-700">
                                      Severity C %
                                    </th>
                                    <th className="w-24 border-b px-3 py-2 text-right text-sm font-medium text-slate-700">
                                      Total
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {scenarioCategories.map((category) => {
                                    const row =
                                      scenario.damageSplitByCategory?.[
                                        category
                                      ] ||
                                      scenario.damageSplit ||
                                      DEFAULT_SCENARIO.damageSplit;
                                    const severityA = Number(row.A) || 0;
                                    const severityB = Number(row.B) || 0;
                                    const severityC = Number(row.C) || 0;
                                    const total =
                                      severityA + severityB + severityC;

                                    return (
                                      <tr
                                        key={category}
                                        className="border-b border-slate-100 last:border-b-0"
                                      >
                                        <td className="px-3 py-2 text-sm font-medium text-slate-800">
                                          {category}
                                        </td>
                                        <td className="w-32 px-3 py-2 text-center">
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={severityA}
                                            onChange={(event) =>
                                              handleDamageSplitByCategoryChange(
                                                category,
                                                "A",
                                                event.target.value,
                                              )
                                            }
                                            className="mx-auto block w-16 rounded-lg border border-slate-300 px-2 py-1 text-right text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                          />
                                        </td>
                                        <td className="w-32 px-3 py-2 text-center">
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={severityB}
                                            onChange={(event) =>
                                              handleDamageSplitByCategoryChange(
                                                category,
                                                "B",
                                                event.target.value,
                                              )
                                            }
                                            className="mx-auto block w-16 rounded-lg border border-slate-300 px-2 py-1 text-right text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                          />
                                        </td>
                                        <td className="w-32 px-3 py-2 text-center">
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={severityC}
                                            onChange={(event) =>
                                              handleDamageSplitByCategoryChange(
                                                category,
                                                "C",
                                                event.target.value,
                                              )
                                            }
                                            className="mx-auto block w-16 rounded-lg border border-slate-300 px-2 py-1 text-right text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                          />
                                        </td>
                                        <td className="px-3 py-2 text-right text-sm text-slate-700">
                                          {total}%
                                          {Math.abs(total - 100) > 0.001 && (
                                            <span className="ml-1 text-red-600">
                                              (≠100)
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            ) : (
                              <p className="text-sm text-slate-500">
                                Load Damage Split Defaults (Corrosion &amp;
                                Damage) to edit per-category splits. Fallback:
                                A=70%, B=20%, C=10%.
                              </p>
                            )
                          ) : null}

                          <p className="mt-2 text-xs text-slate-500">
                            {isDamageSplitMode(scenario)
                              ? "Each element keeps its own exposure class(es); within each class the area is spread across A/B/C using these per-category values when present, otherwise Damage Split Defaults or the single fallback split."
                              : "Damage split percentages are hidden and ignored while Element Damage Level is selected."}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <h3 className="flex flex-wrap items-center gap-2 text-xl font-semibold text-slate-900">
                        <FontAwesomeIcon icon={faCalculator} />
                        <span>Estimated Project Costs</span>
                        {showInlineEstimateRefresh && (
                          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Updating...</span>
                          </span>
                        )}
                        {estimatedCosts?.summary && (
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                            Estimated Total:
                            <span className="ml-2 text-base text-emerald-800">
                              $
                              {Number(displayedEstimatedTotalCost).toLocaleString(
                                undefined,
                                {
                                maximumFractionDigits: 0,
                                },
                              )}
                            </span>
                          </span>
                        )}
                      </h3>
                    </div>

                    <p className="mt-2 text-sm text-slate-600">
                      <span className="font-medium text-slate-700">
                        Selected Team:
                      </span>{" "}
                      {selectedTeamLabel}
                    </p>

                    <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Weeks Limit
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={weeksLimit}
                          onChange={(event) =>
                            setWeeksLimit(event.target.value)
                          }
                          onBlur={() =>
                            setWeeksLimit((previousValue) =>
                              normalizeWeeksLimit(
                                previousValue,
                                defaultWeeksLimit,
                              ),
                            )
                          }
                          className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                        <p className="mt-1.5 text-xs text-slate-500">
                          Default: {defaultWeeksLimit}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                          #Layers per Day
                        </label>
                        <input
                          type="number"
                          min={0.01}
                          step="0.01"
                          value={scenario.layersPerDay}
                          onChange={(event) =>
                            handleScenarioChange(
                              "layersPerDay",
                              event.target.value,
                            )
                          }
                          onBlur={() =>
                            handleScenarioChange(
                              "layersPerDay",
                              normalizePositiveDecimal(
                                scenario.layersPerDay,
                                defaultLayersPerDay,
                              ),
                            )
                          }
                          className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                        <p className="mt-1.5 text-xs text-slate-500">
                          Default: {Number(defaultLayersPerDay).toFixed(2)}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Teams Required
                        </div>
                        <div className="mt-2 text-2xl font-semibold leading-none text-slate-900">
                          {estimatedCosts?.summary?.teamsRequired || 0}
                        </div>
                        <p className="mt-1.5 text-xs text-slate-500">
                          Derived from the week limit.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Weeks Needed
                        </div>
                        <div className="mt-2 text-2xl font-semibold leading-none text-slate-900">
                          {Number(
                            estimatedCosts?.summary?.weeksRequired || 0,
                          ).toFixed(2)}
                        </div>
                        <p className="mt-1.5 text-xs text-slate-500">
                          Calculated duration.
                        </p>
                      </div>
                    </div>

                    {estimateError && estimatedCosts && (
                      <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {estimateError}
                      </div>
                    )}

                    {estimatedCosts && (
                      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
                        <div className="space-y-6">
                          <div>
                            <h4 className="mb-3 font-semibold text-slate-800">
                              Repair Work
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-600">
                                  Selected Area:
                                </span>
                                <span className="font-medium text-slate-900">
                                  {Number(
                                    estimatedCosts.summary?.totalSelectedArea ||
                                      0,
                                  ).toFixed(2)}{" "}
                                  m²
                                </span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-600">
                                  Estimated Painted Area:
                                </span>
                                <span className="font-medium text-slate-900">
                                  {Number(
                                    estimatedCosts.summary
                                      ?.totalEstimatedPaintedArea || 0,
                                  ).toFixed(2)}{" "}
                                  m²
                                </span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-600">
                                  Accumulated Layers Area:
                                </span>
                                <span className="font-medium text-slate-900">
                                  {Number(
                                    estimatedCosts.summary?.totalLayersArea ||
                                      0,
                                  ).toFixed(2)}{" "}
                                  m²
                                </span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-600">
                                  Washing Cost:
                                </span>
                                <span className="font-medium text-slate-900">
                                  $
                                  {Number(
                                    estimatedCosts.summary?.washingCostTotal ||
                                      0,
                                  ).toLocaleString(undefined, {
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-600">
                                  Surface Prep Cost:
                                </span>
                                <span className="font-medium text-slate-900">
                                  $
                                  {Number(
                                    estimatedCosts.summary
                                      ?.surfacePrepCostTotal || 0,
                                  ).toLocaleString(undefined, {
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-600">
                                  Material Cost:
                                </span>
                                <span className="font-medium text-slate-900">
                                  $
                                  {Number(
                                    estimatedCosts.summary?.materialCostTotal ||
                                      0,
                                  ).toLocaleString(undefined, {
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between gap-4 border-t border-slate-200 pt-2">
                                <span className="font-semibold text-slate-900">
                                  Process Subtotal:
                                </span>
                                <span className="font-semibold text-slate-900">
                                  $
                                  {Number(
                                    estimatedCosts.summary
                                      ?.repairProcessCostTotal || 0,
                                  ).toLocaleString(undefined, {
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="mb-3 font-semibold text-slate-800">
                              Mobilization Cost Breakdown
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-600">
                                  Team Labor:
                                </span>
                                <span className="font-medium text-slate-900">
                                  $
                                  {Number(
                                    estimatedCosts.summary?.teamLaborTotal || 0,
                                  ).toLocaleString(undefined, {
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-slate-600">
                                  General Costs:
                                </span>
                                <span className="font-medium text-slate-900">
                                  $
                                  {Number(displayedGeneralCostsTotal).toLocaleString(
                                    undefined,
                                    {
                                    maximumFractionDigits: 0,
                                    },
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between gap-4 border-t border-slate-200 pt-2">
                                <span className="font-semibold text-slate-900">
                                  Total Mobilization:
                                </span>
                                <span className="font-semibold text-slate-900">
                                  $
                                  {Number(
                                    displayedEstimatedMobilizationCost,
                                  ).toLocaleString(undefined, {
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between gap-4 border-t border-slate-200 pt-2">
                                <span className="text-base font-semibold text-slate-900">
                                  Estimated Total:
                                </span>
                                <span className="text-base font-semibold text-emerald-700">
                                  $
                                  {Number(displayedEstimatedTotalCost).toLocaleString(
                                    undefined,
                                    {
                                    maximumFractionDigits: 0,
                                    },
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <h4 className="font-semibold text-slate-800">
                              General Costs
                            </h4>
                            <button
                              type="button"
                              onClick={openGeneralCostDrawer}
                              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                            >
                              <FontAwesomeIcon icon={faPlus} />
                              <span>Add</span>
                            </button>
                          </div>

                          {allRepairGeneralCosts.length > 0 ? (
                            <div className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                              {allRepairGeneralCosts.map((item, index) => {
                                const normalizedItemId = Number(item.id);
                                const isSelected =
                                  normalizedItemId > 0
                                    ? selectedGeneralCostIdSet.has(
                                        normalizedItemId,
                                      )
                                    : item.isSelected !== false;
                                const isPending = pendingGeneralCostToggleIds.has(
                                  String(normalizedItemId),
                                );

                                return (
                                  <div
                                    key={`${item.id || item.name || "general-cost"}-${index}`}
                                    className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
                                  >
                                    <div className="flex min-w-0 items-center gap-3">
                                      <button
                                        type="button"
                                        role="switch"
                                        aria-checked={isSelected}
                                        disabled={
                                          isPending ||
                                          isAnyGeneralCostTogglePending ||
                                          !effectiveSelectedMobilizationConfigId
                                        }
                                        onClick={() =>
                                          handleToggleRepairGeneralCost(
                                            normalizedItemId,
                                          )
                                        }
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                          isSelected
                                            ? "bg-blue-600"
                                            : "bg-gray-300"
                                        } disabled:cursor-not-allowed disabled:opacity-60`}
                                        title={
                                          isSelected
                                            ? "Disable this general cost for the selected team config"
                                            : "Enable this general cost for the selected team config"
                                        }
                                      >
                                        <span
                                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            isSelected
                                              ? "translate-x-6"
                                              : "translate-x-1"
                                          }`}
                                        />
                                      </button>
                                      <span
                                        className={`truncate ${
                                          isSelected
                                            ? "text-slate-700"
                                            : "text-slate-400"
                                        }`}
                                      >
                                        {item.name || "General Cost"}
                                      </span>
                                    </div>
                                    <span
                                      className={`font-medium ${
                                        isSelected
                                          ? "text-slate-900"
                                          : "text-slate-400"
                                      }`}
                                    >
                                      $
                                      {Number(
                                        item.convertedTotal ||
                                          item.total ||
                                          item.amount ||
                                          0,
                                      ).toLocaleString(undefined, {
                                        maximumFractionDigits: 0,
                                      })}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                              No general costs are available for the selected
                              mobilization configuration.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {showGeneralCostDrawer && (
            <div className="fixed inset-0 z-[90] overflow-y-auto bg-gray-600 bg-opacity-50 p-4">
              <div className="relative mx-auto mt-10 w-full max-w-4xl rounded border bg-white shadow-lg">
                <GeneralCostEditorForm
                  title="Add General Cost"
                  formData={generalCostForm}
                  setFormData={setGeneralCostForm}
                  yearOptions={
                    generalCostYearOptions.length > 0
                      ? generalCostYearOptions
                      : generalCostFallbackYearOptions
                  }
                  exposureClassOptions={exposureClassOptions}
                  exposureCodeById={exposureCodeById}
                  defaultSystemCurrency={defaultSystemCurrency}
                  saving={savingGeneralCost}
                  isEditing={false}
                  onClose={closeGeneralCostDrawer}
                  onSave={handleSaveGeneralCost}
                  calculateGeneralCostTotal={calculateGeneralCostTotal}
                />
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateRepairProject;
